/**
 * @fileoverview Hook for subscribing to eligible migration tenants from Dexie.
 * @module system-web/hooks/useEligibleTenants
 *
 * Responsibilities:
 * - Subscribe to all tenants via Dexie liveQuery.
 * - Run the isTenantEligibleForMigration predicate client-side to produce three buckets:
 *   eligible, brokenLink (visible-disabled), and hidden counts.
 * - Expose a refreshed reactive list whenever Dexie changes.
 *
 * Dependencies:
 * - dexie (liveQuery)
 * - @firesquirrel/local-db (getSystemDb)
 * - isTenantEligibleForMigration (inlined predicate — no external package)
 *
 * Architecture:
 * - Local-first: reads from system Dexie `tenants` table — never calls the API directly.
 * - The server re-applies the same eligibility check on every migration route (§3.8.4).
 *
 * Notes:
 * - brokenLink tenants are shown disabled with a tooltip in EligibleTenantPicker.
 * - All other ineligible reasons are hidden; only the count is surfaced in a footer.
 * - This hook does NOT fetch from /eligible-tenants route — that route is for sync only.
 */

import { useState, useEffect, useRef } from 'react';
import { liveQuery } from 'dexie';
import { getSystemDb } from '@firesquirrel/local-db';

// ---------------------------------------------------------------------------
// Eligibility predicate (inlined — no external package dependency)
// ---------------------------------------------------------------------------

const VALID_LEGACY_DBS = new Set(['SLInspectionsDB001', 'SLInspectionsDB002']);

/** Tenant statuses that are allowed as migration targets (mirrors the server predicate). */
const ELIGIBLE_STATUSES = new Set(['active', 'demo']);

type EligibilityResult =
  | { eligible: true; legacyDb: 'SLInspectionsDB001' | 'SLInspectionsDB002'; legacyDeptId: number }
  | { eligible: false; reason: 'not-linked' | 'broken-link' | 'provisioning' | 'wrong-status' | 'soft-deleted' | 'unknown' };

/**
 * Mirror of the authoritative isTenantEligibleForMigration in
 * `@firesquirrel/legacy-migration` (core/tenantEligibility.ts). It is inlined here
 * only because system-web cannot import the api package. It MUST stay field-for-field
 * in sync with the server predicate — read the CANONICAL tenant shape
 * (legacySystem.isLinked / databaseServer), never the onboarding draft shape.
 */
function isTenantEligibleForMigration(row: any): EligibilityResult {
  if (row._deleted === true) return { eligible: false, reason: 'soft-deleted' };
  if (row.status === 'provisioning') return { eligible: false, reason: 'provisioning' };
  if (!ELIGIBLE_STATUSES.has(row.status)) return { eligible: false, reason: 'wrong-status' };
  const ls = row.legacySystem;
  if (!ls || ls.isLinked !== true) return { eligible: false, reason: 'not-linked' };
  const db = ls.databaseServer;
  const deptId = typeof ls.departmentId === 'number'
    ? ls.departmentId
    : typeof ls.departmentId === 'string' && /^\d+$/.test(ls.departmentId.trim())
      ? Number(ls.departmentId)
      : Number.NaN;
  if (!db || !VALID_LEGACY_DBS.has(db)) return { eligible: false, reason: 'broken-link' };
  if (!Number.isInteger(deptId) || deptId <= 0) {
    return { eligible: false, reason: 'broken-link' };
  }
  return { eligible: true, legacyDb: db as 'SLInspectionsDB001' | 'SLInspectionsDB002', legacyDeptId: deptId };
}

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract EligibleTenantEntry
 *
 * Purpose:
 * - Projection of a Dexie tenant row enriched with eligibility result.
 *
 * Fields:
 * - _id: Tenant document ID.
 * - name: Department display name.
 * - legacyDb: Resolved legacy database server.
 * - legacyDeptId: Resolved legacy department ID.
 *
 * Rules:
 * - Only included in the `eligible` bucket when isTenantEligibleForMigration returns eligible=true.
 *
 * Sync:
 * - Source: system Dexie `tenants` table, mirrored from system Mongo.
 */
export interface EligibleTenantEntry {
  _id: string;
  name: string;
  legacyDb: 'SLInspectionsDB001' | 'SLInspectionsDB002';
  legacyDeptId: number;
  legacyTenantId?: number | null;
  legacyDeptCode?: string | null;
}

/**
 * @contract BrokenLinkTenantEntry
 *
 * Purpose:
 * - Projection of a Dexie tenant that has legacySystem.isLinked=true but incomplete linkage.
 *
 * Fields:
 * - _id, name, reason: Always 'broken-link'.
 * - legacyDeptId, legacyTenantId, legacyDeptCode: Optional metadata if present.
 *
 * Rules:
 * - Rendered disabled with tooltip in EligibleTenantPicker.
 *
 * Sync:
 * - Source: system Dexie `tenants` table.
 */
export interface BrokenLinkTenantEntry {
  _id: string;
  name: string;
  reason: 'broken-link';
  legacyDeptId?: number | null;
  legacyTenantId?: number | null;
  legacyDeptCode?: string | null;
}

/**
 * @contract EligibleTenantsState
 *
 * Purpose:
 * - Shape returned by useEligibleTenants.
 *
 * Fields:
 * - eligible: Tenants that pass all eligibility checks.
 * - brokenLink: Tenants that are linked but have incomplete linkage data (visible disabled).
 * - hiddenCount: Count of tenants that are hidden (not linked, wrong status, provisioning, soft-deleted).
 * - isLoading: True while the initial Dexie query is resolving.
 * - error: Non-null when the Dexie subscription fails.
 *
 * Rules:
 * - eligible + brokenLink + hiddenCount accounts for all non-deleted tenants.
 *
 * Sync:
 * - Reactive via liveQuery on system Dexie `tenants`.
 */
export interface EligibleTenantsState {
  eligible: EligibleTenantEntry[];
  brokenLink: BrokenLinkTenantEntry[];
  hiddenCount: number;
  isLoading: boolean;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @hook useEligibleTenants
 *
 * Purpose:
 * - Provide reactive eligible tenant buckets for the EligibleTenantPicker and migration page.
 *
 * Returns:
 * - eligible, brokenLink, hiddenCount, isLoading, error
 *
 * Side Effects:
 * - Subscribes a Dexie liveQuery on `tenants` (torn down on unmount).
 *
 * Offline Behavior:
 * - Fully offline-capable — reads from system Dexie.
 */
export function useEligibleTenants(): EligibleTenantsState {
  const [eligible, setEligible] = useState<EligibleTenantEntry[]>([]);
  const [brokenLink, setBrokenLink] = useState<BrokenLinkTenantEntry[]>([]);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; };
  }, []);

  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;
    let cancelled = false;

    getSystemDb()
      .then((db) => {
        if (cancelled) return;
        subscription = liveQuery(async () => {
          const all = await db.tenants.toArray();
          return all as any[];
        }).subscribe({
          next: (rows: any[]) => {
            if (cancelled || unmountedRef.current) return;

            const eligibleList: EligibleTenantEntry[] = [];
            const brokenLinkList: BrokenLinkTenantEntry[] = [];
            let hidden = 0;

            for (const row of rows) {
              const result = isTenantEligibleForMigration(row);
              const deptData = row.legacySystem?.departmentData;
              const legacyTenantId = typeof deptData?.TenantId === 'number'
                ? deptData.TenantId
                : typeof row.legacySystem?.tenantId === 'number'
                ? row.legacySystem.tenantId
                : null;
              const legacyDeptCode = typeof deptData?.dept_id === 'string'
                ? deptData.dept_id
                : typeof deptData?.dept_code === 'string'
                ? deptData.dept_code
                : null;

              if (result.eligible) {
                eligibleList.push({
                  _id: row._id,
                  name: typeof row.name === 'string' ? row.name : row._id,
                  legacyDb: result.legacyDb,
                  legacyDeptId: result.legacyDeptId,
                  legacyTenantId,
                  legacyDeptCode,
                });
              } else if (result.reason === 'broken-link') {
                brokenLinkList.push({
                  _id: row._id,
                  name: typeof row.name === 'string' ? row.name : row._id,
                  reason: 'broken-link',
                  legacyDeptId: typeof row.legacySystem?.departmentId === 'number' ? row.legacySystem.departmentId : null,
                  legacyTenantId,
                  legacyDeptCode,
                });
              } else {
                hidden += 1;
              }
            }

            // Sort eligible alphabetically by name
            eligibleList.sort((a, b) => a.name.localeCompare(b.name));
            brokenLinkList.sort((a, b) => a.name.localeCompare(b.name));

            setEligible(eligibleList);
            setBrokenLink(brokenLinkList);
            setHiddenCount(hidden);
            setIsLoading(false);
          },
          error: (err: unknown) => {
            if (cancelled || unmountedRef.current) return;
            const msg = err instanceof Error ? err.message : 'Failed to load tenants';
            console.warn('[useEligibleTenants] liveQuery error:', err);
            setError(msg);
            setIsLoading(false);
          },
        });
      })
      .catch((err) => {
        if (cancelled || unmountedRef.current) return;
        const msg = err instanceof Error ? err.message : 'Failed to open Dexie';
        console.warn('[useEligibleTenants] getSystemDb error:', err);
        setError(msg);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  }, []);

  return { eligible, brokenLink, hiddenCount, isLoading, error };
}
